import * as React from 'react';
import { createLink, LinkComponent } from '@tanstack/react-router';
import {
  mergeProps,
  useFocusRing,
  useHover,
  useLink,
  useObjectRef,
} from 'react-aria';
import type { AriaLinkOptions } from 'react-aria';
import {
  mapIntentToIconColor,
  mapSizeToIconSize,
  mapSizeToTypographyVariant,
} from './button';
import { twMerge } from 'tailwind-merge';
import { ReactNode, SVGProps } from 'react';
import { buttonStyles, ButtonStylesVariants } from './variants';
import { Typography } from '../typography/typography';

interface RACLinkProps
  extends Omit<AriaLinkOptions, 'href'>,
    ButtonStylesVariants {
  children?: React.ReactNode;
  isLoading?: boolean;
  className?: string;
  iconLeft?: (props: SVGProps<SVGSVGElement>) => ReactNode;
  iconRight?: (props: SVGProps<SVGSVGElement>) => ReactNode;
}

const RACLinkComponent = React.forwardRef<HTMLAnchorElement, RACLinkProps>(
  (props, forwardedRef) => {
    const {
      iconLeft: IconLeft,
      iconRight: IconRight,
      className,
      size,
      intent,
      isDisabled,
      ...rest
    } = props;
    const ref = useObjectRef(forwardedRef);

    const { isPressed, linkProps } = useLink(rest, ref);
    const { isHovered, hoverProps } = useHover(rest);
    const { isFocusVisible, isFocused, focusProps } = useFocusRing(rest);

    return (
      <a
        {...mergeProps(linkProps, hoverProps, focusProps, rest)}
        ref={ref}
        data-hovered={isHovered || undefined}
        data-pressed={isPressed || undefined}
        data-focus-visible={isFocusVisible || undefined}
        data-focused={isFocused || undefined}
        aria-disabled={isDisabled}
        data-disabled={isDisabled}
        className={twMerge(buttonStyles({ intent, size }), className)}
      >
        {IconLeft && (
          <IconLeft
            className={twMerge(
              mapSizeToIconSize(size),
              mapIntentToIconColor(intent),
              'mr-4',
            )}
          />
        )}

        <Typography
          as="span"
          variant={mapSizeToTypographyVariant(size)}
          weight="medium"
          useCase="button"
          className="text-currentColor"
        >
          {rest.children}
        </Typography>
        {IconRight && (
          <IconRight
            className={twMerge(
              mapSizeToIconSize(size),
              mapIntentToIconColor(intent),
              'ml-4',
            )}
          />
        )}
      </a>
    );
  },
);

const CreatedLinkComponent = createLink(RACLinkComponent);

export const ButtonLink: LinkComponent<typeof RACLinkComponent> = (props) => {
  return <CreatedLinkComponent preload={'intent'} {...props} />;
};
